import { useEffect, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';

import { type AvatarUploadRef, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import type { Institution } from 'modules/institutions/core/type';

import type { Trainer } from '../type';

import { getExtFileName, getInputStatus, isValidEmail, randomString, trimObject } from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultAddTrainerForm } from '../constant';
import { formTrainerSchema } from '../schema';

export const useFullTimeFormController = () => {
  const userProfile = useAuthStore(userProfileSelector);
  const navigation = useNavigate();
  const institutionId = userProfile?.institution?.id || '';
  const roleKey = userProfile?.role?.key || '';

  const trainerRole = useFetchApiToState<any, any>({
    url: '/trainer-roles',
    method: 'GET',
  });
  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const logoRef = useRef<AvatarUploadRef>(null);

  const logoURL = useString('');

  const [logo, setLogo] = useState<File | null>(null);
  const [invalidPhoneMsg, setInvalidPhoneMsg] = useState<string | null>(null);

  const isRemoveLogo = useBoolean(false);

  const searchSelect = useString('');

  const levels =
    trainerRole.state?.map((elRole: any) => ({
      key: String(elRole.id),
      label: elRole.name,
    })) || [];

  const institutions =
    institutionState.state?.items?.map((elInstitutions: Institution) => ({
      value: elInstitutions.id,
      label: elInstitutions.name,
    })) || [];

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    if (roleKey === RoleKey.BIOLOGIC_ADMIN) {
      institutionState.fetchApi();
    }

    trainerRole.fetchApi();
  }, [roleKey]);

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    let newInstitutionId = institutionId;

    if (!isEmpty(institutions) && roleKey === RoleKey.BIOLOGIC_ADMIN) {
      newInstitutionId = institutions[0].value;
    }

    setFieldValue('institutionId', newInstitutionId);
  }, [JSON.stringify(institutions), roleKey]);

  const formik = useFormik({
    initialValues: defaultAddTrainerForm,
    validationSchema: formTrainerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.institutionId) {
        return;
      }

      setSubmitting(true);

      let avatar = '';

      if (values.firstName.length + values.lastName.length > 80) {
        showToastMessage('error', 'Maximum of 80 characters for the name');

        return;
      }

      if (logo) {
        const ext = getExtFileName(logo.name);

        avatar = `trainer-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(logo, avatar);
      }

      const result: Trainer = await apiCall({
        url: '/users/trainer-institution',
        data: {
          ...trimObject(values),
          level: Number(values.level),
          avatar,
          name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        },
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (!isEmpty(result?.id)) {
        navigation(PATH.TRAINERS);
        showToastMessage('success', 'Added Trainer successfully!');
      }
    },
  });
  const { values, isSubmitting, isValid, handleSubmit, handleChange, setFieldValue } = formik;

  useEffect(() => {
    if (isEmpty(levels)) {
      return;
    }

    setFieldValue('level', levels[0].key);
  }, [JSON.stringify(levels)]);

  const onChangeLevel = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('level', item.key);
  };

  const onChangeInstitution = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('institutionId', item.value);
  };

  const onChangeSearchInstitution = (item: string) => {
    searchSelect.setValue(item);
  };

  const onChangeGender = (newGenderKey: string) => () => {
    setFieldValue('gender', newGenderKey);
  };

  const handleChangePhone = (value: string | undefined) => {
    setFieldValue('phone', value);
  };

  const emailStatus = getInputStatus(
    values.email,
    SUCCESS_MESSAGE.EMAIL,
    isValidEmail(values.email.trim()).isValid ? '' : isValidEmail(values.email.trim()).message,
  );

  const disabled =
    isSubmitting ||
    !isValid ||
    (isValidEmail(values.email.trim()).isValid ? false : true) ||
    !isValidPhoneNumber(values.phone);

  const goBack = () => {
    navigation(PATH.TRAINERS);
  };

  const onChangeAvatar = (file: File) => {
    setLogo(file);
  };

  const onDeleteLogo = () => {
    logoRef.current?.clearAvatar();
    setLogo(null);

    if (logoURL.value) {
      isRemoveLogo.setValue(true);
    }
  };

  return {
    ...values,
    roleKey,
    disabled,
    levels,
    institutions,
    emailStatus,
    handleChange,
    onChangeLevel,
    onChangeGender,
    handleSubmit,
    handleChangePhone,
    onChangeInstitution,
    goBack,
    logoRef,
    onChangeAvatar,
    logoURL,
    onDeleteLogo,
    onChangeSearchInstitution,
    searchSelect,
    invalidPhoneMsg,
  };
};
